import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { useTitle, formatDate, formatTime, formatPrice } from "@/lib/format";
import { PageLoader, StatusBadge, DemoBadge, EmptyState } from "@/components/common";

export default function BookingConfirmed() {
  useTitle("Booking Confirmed");
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => { api.get(`/bookings/${id}`).then(({ data }) => setB(data)).catch(() => setError(true)); }, [id]);
  if (error) return <div className="mx-auto max-w-xl px-4 py-20"><EmptyState icon={AlertTriangle} title="Booking not found" text="This booking does not exist or you don't have access to it." testId="confirmed-not-found" /></div>;
  if (!b) return <PageLoader />;
  const rows = [["Provider", b.provider_name], ["Service", b.service_name], ["Date", formatDate(b.date)], ["Time", `${formatTime(b.start_time)} - ${formatTime(b.end_time)}`], ["Location", b.address || b.location], ["Price", `${formatPrice(b.price)} NZD`]];
  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:py-16" data-testid="booking-confirmed-page">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_25px_50px_-12px_rgba(15,23,42,0.12)]">
        <div className="bg-gradient-to-br from-sky-600 to-teal-600 px-6 py-10 text-center text-white">
          <motion.div initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.1 }}>
            <CheckCircle2 className="mx-auto h-16 w-16" />
          </motion.div>
          <h1 className="mt-4 font-heading text-3xl font-bold sm:text-4xl" data-testid="booking-confirmed-heading">Booking Confirmed</h1>
          <p className="mt-2 text-white/85">Your request has been sent to {b.provider_name}.</p>
        </div>
        <div className="p-6 sm:p-8">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-slate-50 p-4">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-500">Booking reference</p>
              <p className="font-mono text-2xl font-bold tracking-wider text-slate-900" data-testid="booking-reference">{b.reference}</p>
            </div>
            <div className="flex gap-2"><StatusBadge status={b.status} testId="booking-status" />{b.is_demo && <DemoBadge />}</div>
          </div>
          <dl className="mt-6 divide-y divide-slate-100">
            {rows.map(([k, v]) => (
              <div key={k} className="flex justify-between gap-4 py-3 text-sm"><dt className="text-slate-500">{k}</dt><dd className="text-right font-semibold text-slate-900" data-testid={`confirmed-${k.toLowerCase()}`}>{v}</dd></div>
            ))}
          </dl>
          <p className="mt-4 text-xs text-slate-500">The provider will confirm your appointment. A confirmation email has been sent where possible.</p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Link to="/bookings" data-testid="go-my-bookings" className="flex h-12 items-center justify-center rounded-xl bg-sky-600 font-semibold text-white hover:bg-sky-700">Manage in My Bookings</Link>
            <Link to="/services" className="flex h-12 items-center justify-center rounded-xl border border-slate-300 font-semibold text-slate-700" data-testid="find-another-service">Find another service</Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
